/**
 * Handover-readiness rules (V2-E6.1 / HAN-64). A patient is "ready" to hand over
 * when its SBAR is fully written and — for high-priority patients — it carries at
 * least one outstanding task. These are the same completeness rules the patient
 * form enforces at create/update time ({@link patientInputSchema} in `patient.ts`);
 * pulled out as a pure, dependency-light function so both the API (the
 * `GET /sessions/:id/readiness` endpoint and the server-side code-generation
 * guard) and the web checklist can evaluate them identically.
 *
 * Kept free of zod and any API/drizzle imports so it can run anywhere.
 */
import type { Priority } from "../db.js";

/** The high-priority-needs-a-task message, shared with the form's superRefine. */
export const HIGH_PRIORITY_TASK_MESSAGE = "A high-priority patient needs at least one task.";

/** The four SBAR fields, as plain strings (post-persistence, may be untrimmed). */
export interface ReadinessSbar {
  situation: string;
  background: string;
  assessment: string;
  recommendation: string;
}

/** The minimal patient shape the readiness rules need. */
export interface PatientReadinessInput {
  priority: Priority;
  sbar: ReadinessSbar;
  /** Number of tasks currently on the patient. */
  taskCount: number;
}

/** Why a patient is not yet ready to hand over. */
export type PatientReadinessIssueCode = "sbar-incomplete" | "high-priority-task-missing";

export interface PatientReadinessIssue {
  code: PatientReadinessIssueCode;
  /** Human-readable reason, safe to show to the outgoing doctor. */
  message: string;
}

export interface PatientReadinessResult {
  /** All four SBAR fields are non-empty after trimming. */
  sbarComplete: boolean;
  /** Tasks currently on the patient (echoed for the checklist). */
  taskCount: number;
  /** No blocking issues — this patient can be handed over. */
  complete: boolean;
  /** Blocking issues; empty when `complete`. */
  issues: PatientReadinessIssue[];
}

function isFilled(value: string): boolean {
  return value.trim().length > 0;
}

/**
 * Evaluate a single patient's handover readiness. Returns the per-field flags the
 * checklist renders plus the blocking issues that disable the generate CTA (and
 * that the API mirrors when it refuses code generation).
 */
export function evaluatePatientReadiness(input: PatientReadinessInput): PatientReadinessResult {
  const { priority, sbar, taskCount } = input;

  const sbarComplete =
    isFilled(sbar.situation) &&
    isFilled(sbar.background) &&
    isFilled(sbar.assessment) &&
    isFilled(sbar.recommendation);

  const issues: PatientReadinessIssue[] = [];
  if (!sbarComplete) {
    issues.push({ code: "sbar-incomplete", message: "The SBAR handover is incomplete." });
  }
  if (priority === "high" && taskCount < 1) {
    issues.push({ code: "high-priority-task-missing", message: HIGH_PRIORITY_TASK_MESSAGE });
  }

  return { sbarComplete, taskCount, complete: issues.length === 0, issues };
}
